import React, { useState, useEffect, useMemo } from 'react';
import { Download, Loader2, Plus, X, Save, Trash2, Play, Table, Grid3X3, ChevronDown } from 'lucide-react';
import { customReportsAPI } from '../../services/api';
import DateRangeFilter from './DateRangeFilter';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

const DIMENSION_CATEGORIES = ['Time', 'Location', 'People', 'Product', 'Source'];

const CustomReportBuilder = () => {
  const [dimensions, setDimensions] = useState([]);
  const [measures, setMeasures] = useState([]);
  const [savedReports, setSavedReports] = useState([]);
  const [selectedDimensions, setSelectedDimensions] = useState([]);
  const [selectedMeasures, setSelectedMeasures] = useState(['amount']);
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'pivot'
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [reportName, setReportName] = useState('');

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [dimRes, measRes, savedRes] = await Promise.all([
        customReportsAPI.getDimensions(),
        customReportsAPI.getMeasures(),
        customReportsAPI.getSavedReports(),
      ]);
      setDimensions(dimRes.dimensions || []);
      setMeasures(measRes.measures || []);
      setSavedReports(savedRes.items || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load report options');
    } finally {
      setLoading(false);
    }
  };

  const dimensionsByCategory = useMemo(() => {
    const grouped = {};
    dimensions.forEach(d => {
      const cat = d.category || 'Other';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(d);
    });
    return grouped;
  }, [dimensions]);

  const MAX_DIMENSIONS = 8;

  const addDimension = (dimId) => {
    if (selectedDimensions.length >= MAX_DIMENSIONS) {
      toast.warning(`Maximum ${MAX_DIMENSIONS} dimensions allowed`);
      return;
    }
    if (selectedDimensions.includes(dimId)) return;
    setSelectedDimensions([...selectedDimensions, dimId]);
  };

  const removeDimension = (dimId) => {
    setSelectedDimensions(selectedDimensions.filter(d => d !== dimId));
  };

  const toggleMeasure = (measId) => {
    if (selectedMeasures.includes(measId)) {
      if (selectedMeasures.length === 1) {
        toast.warning('At least 1 measure required');
        return;
      }
      setSelectedMeasures(selectedMeasures.filter(m => m !== measId));
    } else {
      setSelectedMeasures([...selectedMeasures, measId]);
    }
  };

  // Convert a Date/null to YYYY-MM-DD using local time (avoids the UTC-offset bug
  // where dateRange.from gets serialized to an ISO string starting with the previous day
  // in IST, which fails the string-comparison against `dispatch_date` ("YYYY-MM-DD") on the backend).
  const toYMD = (d) => {
    if (!d) return null;
    const dt = d instanceof Date ? d : new Date(d);
    if (isNaN(dt.getTime())) return null;
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const day = String(dt.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const generateReport = async () => {
    if (selectedDimensions.length === 0) {
      toast.warning('Select at least 1 dimension');
      return;
    }
    try {
      setGenerating(true);
      const config = {
        dimensions: selectedDimensions,
        measures: selectedMeasures,
        from_date: toYMD(dateRange.from),
        to_date: toYMD(dateRange.to),
      };
      const result = await customReportsAPI.generate(config);
      setReportData(result);
      // Auto-switch to pivot if 2 dimensions
      if (selectedDimensions.length === 2 && result.pivot) {
        setViewMode('pivot');
      } else {
        setViewMode('table');
      }
      toast.success(`Report generated: ${result.meta.row_count} rows`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const saveReport = async () => {
    if (!reportName.trim()) {
      toast.warning('Enter a report name');
      return;
    }
    try {
      setSaving(true);
      await customReportsAPI.saveReport({
        name: reportName,
        dimensions: selectedDimensions,
        measures: selectedMeasures,
        filters: { from_date: toYMD(dateRange.from), to_date: toYMD(dateRange.to) },
      });
      toast.success('Report saved');
      setShowSaveDialog(false);
      setReportName('');
      // Refresh saved reports
      const savedRes = await customReportsAPI.getSavedReports();
      setSavedReports(savedRes.items || []);
    } catch (err) {
      toast.error('Failed to save report');
    } finally {
      setSaving(false);
    }
  };

  const loadSavedReport = (report) => {
    setSelectedDimensions(report.dimensions || []);
    setSelectedMeasures(report.measures || ['amount']);
    if (report.filters) {
      setDateRange({
        from: report.filters.from_date || null,
        to: report.filters.to_date || null,
      });
    }
    toast.success(`Loaded: ${report.name}`);
  };

  const deleteSavedReport = async (reportId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this saved report?')) return;
    try {
      await customReportsAPI.deleteReport(reportId);
      setSavedReports(savedReports.filter(r => r.id !== reportId));
      toast.success('Report deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const exportToExcel = () => {
    if (!reportData?.rows?.length) return;
    
    const wb = XLSX.utils.book_new();
    
    // Data sheet
    const headers = [
      ...selectedDimensions.map(d => dimensions.find(dim => dim.id === d)?.label || d),
      ...selectedMeasures.map(m => measures.find(meas => meas.id === m)?.label || m),
    ];
    const rows = reportData.rows.map(row => [
      ...selectedDimensions.map(d => row[d]),
      ...selectedMeasures.map(m => row[m]),
    ]);
    const dataAoA = [headers, ...rows];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(dataAoA), 'Data');
    
    // Pivot sheet if available
    if (reportData.pivot && selectedDimensions.length === 2) {
      const pivot = reportData.pivot;
      const pivotHeaders = ['', ...pivot.col_keys, 'Total'];
      const pivotRows = pivot.row_keys.map(rk => [
        rk,
        ...pivot.col_keys.map(ck => pivot.grid[rk]?.[ck] || 0),
        pivot.row_totals[rk] || 0,
      ]);
      const totalsRow = ['Total', ...pivot.col_keys.map(ck => pivot.col_totals[ck] || 0), pivot.grand_total];
      const pivotAoA = [pivotHeaders, ...pivotRows, totalsRow];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(pivotAoA), 'Pivot');
    }
    
    XLSX.writeFile(wb, `custom_report_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success('Excel exported');
  };

  const getDimensionLabel = (dimId) => dimensions.find(d => d.id === dimId)?.label || dimId;
  const getMeasureLabel = (measId) => measures.find(m => m.id === measId)?.label || measId;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#24549c' }} />
      </div>
    );
  }

  return (
    <div data-testid="custom-report-builder" className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>
            Custom Report Builder
          </h2>
          <p className="text-xs text-gray-500">Build your own reports with flexible dimensions and measures</p>
        </div>
        <div className="flex items-center gap-2">
          {reportData?.rows?.length > 0 && (
            <>
              <button
                onClick={() => setShowSaveDialog(true)}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <Save className="w-3.5 h-3.5" /> Save Config
              </button>
              <button
                onClick={exportToExcel}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white rounded-lg"
                style={{ backgroundColor: '#24549c' }}
              >
                <Download className="w-3.5 h-3.5" /> Export Excel
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Left Panel - Config */}
        <div className="lg:col-span-1 space-y-4">
          {/* Saved Reports */}
          {savedReports.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <h3 className="text-xs font-semibold text-gray-700 mb-2">Saved Reports</h3>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {savedReports.map(r => (
                  <div
                    key={r.id}
                    onClick={() => loadSavedReport(r)}
                    className="flex items-center justify-between px-2 py-1.5 text-xs rounded-lg hover:bg-gray-50 cursor-pointer group"
                  >
                    <span className="truncate">{r.name}</span>
                    <button
                      onClick={(e) => deleteSavedReport(r.id, e)}
                      className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Dimensions */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <h3 className="text-xs font-semibold text-gray-700 mb-2">
              Dimensions <span className="text-gray-400">({selectedDimensions.length}/4)</span>
            </h3>
            
            {/* Selected Dimensions */}
            {selectedDimensions.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-3 p-2 bg-blue-50 rounded-lg">
                {selectedDimensions.map((dimId, idx) => (
                  <span
                    key={dimId}
                    className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-white rounded-full"
                    style={{ backgroundColor: '#24549c' }}
                  >
                    {idx + 1}. {getDimensionLabel(dimId)}
                    <button onClick={() => removeDimension(dimId)} className="hover:bg-white/20 rounded-full">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            
            {/* Dimension Categories */}
            {DIMENSION_CATEGORIES.map(cat => (
              <div key={cat} className="mb-2">
                <p className="text-[10px] font-semibold text-gray-400 uppercase mb-1">{cat}</p>
                <div className="flex flex-wrap gap-1">
                  {(dimensionsByCategory[cat] || []).map(dim => {
                    const isSelected = selectedDimensions.includes(dim.id);
                    return (
                      <button
                        key={dim.id}
                        onClick={() => addDimension(dim.id)}
                        disabled={isSelected || selectedDimensions.length >= MAX_DIMENSIONS}
                        className={`px-2 py-1 text-[10px] rounded-lg border transition-all ${
                          isSelected
                            ? 'bg-blue-100 text-blue-700 border-blue-200'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300 disabled:opacity-40'
                        }`}
                      >
                        {dim.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Measures */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <h3 className="text-xs font-semibold text-gray-700 mb-2">Measures</h3>
            <div className="space-y-1">
              {measures.map(meas => {
                const isSelected = selectedMeasures.includes(meas.id);
                return (
                  <label
                    key={meas.id}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleMeasure(meas.id)}
                      className="rounded border-gray-300"
                    />
                    <span className={`text-xs ${isSelected ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
                      {meas.label}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Date Range */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <h3 className="text-xs font-semibold text-gray-700 mb-2">Date Range</h3>
            <DateRangeFilter value={dateRange} onChange={setDateRange} compact />
          </div>

          {/* Generate Button */}
          <button
            onClick={generateReport}
            disabled={generating || selectedDimensions.length === 0}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-white rounded-xl disabled:opacity-40 hover:shadow-lg transition-shadow"
            style={{ backgroundColor: '#24549c' }}
            data-testid="generate-report-btn"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            {generating ? 'Generating...' : 'Generate Report'}
          </button>
        </div>

        {/* Right Panel - Results */}
        <div className="lg:col-span-3">
          {!reportData ? (
            <div className="bg-white rounded-xl border border-gray-100 p-10 text-center">
              <Grid3X3 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-sm text-gray-500">Select dimensions and measures, then click Generate Report</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* View Toggle */}
              {selectedDimensions.length === 2 && reportData.pivot && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">View:</span>
                  <div className="flex bg-gray-100 rounded-lg p-0.5">
                    <button
                      onClick={() => setViewMode('table')}
                      className={`flex items-center gap-1 px-3 py-1.5 text-[10px] font-semibold rounded-md transition-colors ${
                        viewMode === 'table' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'
                      }`}
                    >
                      <Table className="w-3 h-3" /> Flat Table
                    </button>
                    <button
                      onClick={() => setViewMode('pivot')}
                      className={`flex items-center gap-1 px-3 py-1.5 text-[10px] font-semibold rounded-md transition-colors ${
                        viewMode === 'pivot' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'
                      }`}
                    >
                      <Grid3X3 className="w-3 h-3" /> Pivot Table
                    </button>
                  </div>
                  <span className="text-[10px] text-gray-400 ml-2">
                    {reportData.meta.row_count} rows
                  </span>
                </div>
              )}

              {/* Totals Summary */}
              <div className="flex flex-wrap gap-3">
                {selectedMeasures.map(measId => (
                  <div key={measId} className="bg-white rounded-xl border border-gray-100 px-4 py-3">
                    <p className="text-[10px] text-gray-500 font-semibold uppercase">{getMeasureLabel(measId)}</p>
                    <p className="text-lg font-bold text-gray-900">
                      {measId.includes('amount') || measId.includes('rate')
                        ? `₹${(reportData.totals[measId] || 0).toLocaleString('en-IN')}`
                        : (reportData.totals[measId] || 0).toLocaleString('en-IN')}
                    </p>
                  </div>
                ))}
              </div>

              {/* Data Display */}
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                {viewMode === 'pivot' && reportData.pivot ? (
                  <PivotView
                    pivot={reportData.pivot}
                    rowLabel={getDimensionLabel(selectedDimensions[0])}
                    measure={selectedMeasures[0]}
                    getMeasureLabel={getMeasureLabel}
                  />
                ) : (
                  <FlatTableView
                    rows={reportData.rows}
                    dimensions={selectedDimensions}
                    measures={selectedMeasures}
                    getDimensionLabel={getDimensionLabel}
                    getMeasureLabel={getMeasureLabel}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Save Dialog */}
      {showSaveDialog && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-5 w-full max-w-sm">
            <h3 className="text-sm font-bold text-gray-900 mb-3">Save Report Configuration</h3>
            <input
              type="text"
              value={reportName}
              onChange={e => setReportName(e.target.value)}
              placeholder="Report name..."
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg mb-4"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setShowSaveDialog(false); setReportName(''); }}
                className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={saveReport}
                disabled={saving || !reportName.trim()}
                className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold text-white rounded-lg disabled:opacity-40"
                style={{ backgroundColor: '#24549c' }}
              >
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Flat Table View Component
const FlatTableView = ({ rows, dimensions, measures, getDimensionLabel, getMeasureLabel }) => {
  if (!rows?.length) {
    return <div className="p-10 text-center text-sm text-gray-400">No data</div>;
  }
  
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            {dimensions.map(dimId => (
              <th key={dimId} className="px-3 py-3 text-left text-[10px] font-semibold text-gray-600 uppercase">
                {getDimensionLabel(dimId)}
              </th>
            ))}
            {measures.map(measId => (
              <th key={measId} className="px-3 py-3 text-right text-[10px] font-semibold text-gray-600 uppercase">
                {getMeasureLabel(measId)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 500).map((row, idx) => (
            <tr key={idx} className="border-b border-gray-50 hover:bg-gray-50">
              {dimensions.map(dimId => (
                <td key={dimId} className="px-3 py-2 font-medium text-gray-900">
                  {row[dimId] || '—'}
                </td>
              ))}
              {measures.map(measId => (
                <td key={measId} className="px-3 py-2 text-right text-gray-700">
                  {measId.includes('amount') || measId.includes('rate')
                    ? `₹${(row[measId] || 0).toLocaleString('en-IN')}`
                    : (row[measId] || 0).toLocaleString('en-IN')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > 500 && (
        <div className="px-4 py-2 text-xs text-amber-600 bg-amber-50 border-t">
          Showing first 500 rows. Export to Excel for all {rows.length} rows.
        </div>
      )}
    </div>
  );
};

// Pivot Table View Component
const PivotView = ({ pivot, rowLabel, measure, getMeasureLabel }) => {
  if (!pivot || !pivot.row_keys?.length) {
    return <div className="p-10 text-center text-sm text-gray-400">No pivot data</div>;
  }
  
  const formatValue = (val) => {
    if (measure.includes('amount') || measure.includes('rate')) {
      return `₹${(val || 0).toLocaleString('en-IN')}`;
    }
    return (val || 0).toLocaleString('en-IN');
  };
  
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr>
            <th className="px-3 py-2 text-left text-[10px] font-semibold text-white uppercase border border-white/10" style={{ backgroundColor: '#24549c' }}>
              {rowLabel}
            </th>
            {pivot.col_keys.map(ck => (
              <th key={ck} className="px-3 py-2 text-center text-[10px] font-semibold text-white uppercase border border-white/10 whitespace-nowrap" style={{ backgroundColor: '#24549c' }}>
                {ck}
              </th>
            ))}
            <th className="px-3 py-2 text-right text-[10px] font-semibold text-white uppercase border border-white/10" style={{ backgroundColor: '#1a3d73' }}>
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          {pivot.row_keys.map(rk => (
            <tr key={rk} className="hover:bg-blue-50/30">
              <td className="px-3 py-2 font-medium text-gray-900 border border-gray-200 bg-gray-50">{rk}</td>
              {pivot.col_keys.map(ck => (
                <td key={ck} className="px-3 py-2 text-center text-gray-700 border border-gray-200">
                  {pivot.grid[rk]?.[ck] ? formatValue(pivot.grid[rk][ck]) : ''}
                </td>
              ))}
              <td className="px-3 py-2 text-right font-semibold text-gray-900 border border-gray-200 bg-amber-50">
                {formatValue(pivot.row_totals[rk])}
              </td>
            </tr>
          ))}
          <tr className="font-bold text-white" style={{ backgroundColor: '#24549c' }}>
            <td className="px-3 py-2 border border-white/10 uppercase text-[10px]">Total</td>
            {pivot.col_keys.map(ck => (
              <td key={ck} className="px-3 py-2 text-center border border-white/10">
                {formatValue(pivot.col_totals[ck])}
              </td>
            ))}
            <td className="px-3 py-2 text-right border border-white/10" style={{ backgroundColor: '#1a3d73' }}>
              {formatValue(pivot.grand_total)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default CustomReportBuilder;
